<template>
  <div class="prompts-page">
    <div class="market-bar">
      <span class="market-label">内容市场</span>
      <el-radio-group v-model="contentMarket" size="small" @change="onContentMarketChange">
        <el-radio-button label="domestic">国内</el-radio-button>
        <el-radio-button label="overseas">海外（TabStory）</el-radio-button>
      </el-radio-group>
    </div>
    <!-- 顶级分区：提示词模板（设置 → 提示词管理）/ 话题池（选题 → 话题池），由 mode 决定显示哪个 -->
    <el-tabs
      v-model="topTab"
      class="top-tabs"
      :class="{ 'single-mode': mode !== 'all' }"
      @tab-change="onTopTabChange"
    >

    <!-- ══════════════ 分区 1：提示词模板 ══════════════ -->
    <el-tab-pane v-if="showTemplatePane" label="提示词模板" name="template">
    <!-- 顶部维度切换 -->
    <div class="scope-tabs">
      <el-radio-group v-model="activeScope" size="default" @change="onScopeChange">
        <el-radio-button v-for="s in scopeOptions" :key="s.value" :value="s.value">
          {{ s.label }}
        </el-radio-button>
      </el-radio-group>

      <div class="toolbar-right">
        <el-button type="primary" :icon="Plus" @click="openCreateDialog">新增模板</el-button>
        <el-button :icon="Refresh" @click="loadList">刷新</el-button>
      </div>
    </div>

    <div class="main-layout">
      <!-- 左栏：条目列表 -->
      <el-card class="col left" shadow="never">
        <template #header>
          <div class="col-header">
            <span>{{ currentScopeLabel }}（{{ filteredItems.length }}）</span>
            <el-input
              v-model="search"
              placeholder="搜索键/标题…"
              size="small"
              style="width: 160px"
              clearable
            />
          </div>
        </template>

        <div class="item-list" v-loading="loadingList">
          <div
            v-for="it in filteredItems"
            :key="itemKey(it)"
            class="item-row"
            :class="{ active: selected && itemKey(it) === itemKey(selected) }"
            @click="selectItem(it)"
          >
            <div class="item-main">
              <span class="item-title">
                {{ displayKey(it) }}
              </span>
              <!-- article_type 视图已按类型分组；知乎问答的 displayKey 已包含 segment 名，避免重复显示 -->
              <span
                v-if="activeScope !== 'article_type' && activeScope !== 'zhihu_qa' && activeScope !== 'column_oral' && activeScope !== 'tabstory' && activeScope !== 'bilibili_rewrite' && activeScope !== 'douyin_rewrite' && activeScope !== 'content_rewrite'"
                class="item-segment"
              >{{ segmentLabel(it.segment) }}</span>
            </div>
            <el-tag
              size="small"
              :type="sourceTagType(it.source)"
              effect="plain"
            >
              {{ sourceLabel(it.source) }}
            </el-tag>
          </div>
          <el-empty v-if="!filteredItems.length" description="暂无数据" :image-size="60" />
        </div>
      </el-card>

      <!-- 中栏：编辑区 -->
      <el-card class="col middle" shadow="never">
        <template #header>
          <div class="col-header">
            <span v-if="selected">
              编辑：<b>{{ displayKey(selected) }}</b>
              <span v-if="selected.scope !== 'article_type' && selected.scope !== 'zhihu_qa' && selected.scope !== 'column_oral' && selected.scope !== 'tabstory'"> / {{ segmentLabel(selected.segment) }}</span>
            </span>
            <span v-else>选择左侧条目进行编辑</span>
            <div v-if="selected" class="header-actions">
              <el-tag v-if="selected.source === 'builtin'" type="info">内置默认</el-tag>
              <el-tag v-else-if="selected.source === 'overridden'" type="warning">覆盖内置</el-tag>
              <el-tag v-else type="success">自定义</el-tag>
            </div>
          </div>
        </template>

        <div v-if="!selected" class="placeholder">
          从左栏选择一个条目开始编辑。若该维度暂无条目，可点击右上角"新增模板"按钮。
        </div>

        <div v-else class="editor">
          <el-form label-width="90px" size="default">
            <el-form-item label="标题">
              <el-input v-model="editForm.title" placeholder="可选：给这条模板起个好记的名字" />
            </el-form-item>
            <el-form-item label="描述">
              <el-input
                v-model="editForm.description"
                type="textarea"
                :rows="2"
                placeholder="可选：这条模板的用途/场景"
              />
            </el-form-item>
            <el-form-item label="模板内容">
              <el-input
                v-model="editForm.content"
                type="textarea"
                :rows="18"
                placeholder="支持 {占位符} 语法，例如 {topic_name}、{platform_name}"
              />
            </el-form-item>
            <el-form-item label="变更说明">
              <el-input
                v-model="editForm.change_note"
                placeholder="可选：本次改动的原因"
                maxlength="120"
                show-word-limit
              />
            </el-form-item>
          </el-form>

          <div class="action-row">
            <el-button type="primary" :icon="Check" @click="saveEdit" :loading="saving">
              保存（创建新版本）
            </el-button>
            <el-button
              v-if="selected.source !== 'builtin'"
              :icon="Clock"
              @click="openVersionDrawer"
            >
              历史版本
            </el-button>
            <el-button
              v-if="selected.source === 'overridden'"
              type="warning"
              :icon="RefreshLeft"
              @click="resetToBuiltin"
            >
              恢复出厂
            </el-button>
            <el-button
              v-if="canDeleteCurrent"
              type="danger"
              :icon="Delete"
              @click="deleteCurrent"
            >
              {{ deleteButtonText }}
            </el-button>
          </div>

          <el-alert
            v-if="availablePlaceholders.length"
            type="info"
            :closable="false"
            class="placeholder-hint"
            show-icon
          >
            <template #title>
              此片段可用占位符：{{ availablePlaceholders.map(p => '{' + p + '}').join(' / ') }}
            </template>
          </el-alert>

          <el-alert
            v-if="selected.scope === 'zhihu_qa' && ZHIHU_QA_SEGMENT_HINTS[selected.segment]"
            type="info"
            :closable="false"
            class="placeholder-hint"
            show-icon
          >
            <template #title>{{ ZHIHU_QA_SEGMENT_HINTS[selected.segment] }}</template>
          </el-alert>

          <el-alert
            v-if="selected.scope === 'column_oral' && COLUMN_ORAL_SEGMENT_HINTS[selected.segment]"
            type="info"
            :closable="false"
            class="placeholder-hint"
            show-icon
          >
            <template #title>{{ COLUMN_ORAL_SEGMENT_HINTS[selected.segment] }}</template>
          </el-alert>

          <el-alert
            v-if="selected.scope === 'tabstory' && TABSTORY_SEGMENT_HINTS[selected.segment]"
            type="info"
            :closable="false"
            class="placeholder-hint"
            show-icon
          >
            <template #title>{{ TABSTORY_SEGMENT_HINTS[selected.segment] }}</template>
          </el-alert>

          <el-alert
            v-if="selected.scope === 'bilibili_rewrite' && BILI_REWRITE_SEGMENT_HINTS[selected.segment]"
            type="info"
            :closable="false"
            class="placeholder-hint"
            show-icon
          >
            <template #title>{{ BILI_REWRITE_SEGMENT_HINTS[selected.segment] }}</template>
          </el-alert>

          <el-alert
            v-if="selected.scope === 'douyin_rewrite' && DOUYIN_REWRITE_SEGMENT_HINTS[selected.segment]"
            type="info"
            :closable="false"
            class="placeholder-hint"
            show-icon
          >
            <template #title>{{ DOUYIN_REWRITE_SEGMENT_HINTS[selected.segment] }}</template>
          </el-alert>

          <el-alert
            v-if="selected.scope === 'content_rewrite' && CONTENT_REWRITE_SEGMENT_HINTS[selected.segment]"
            type="info"
            :closable="false"
            class="placeholder-hint"
            show-icon
          >
            <template #title>{{ CONTENT_REWRITE_SEGMENT_HINTS[selected.segment] }}</template>
          </el-alert>

          <!-- 文章类型：切入角度库 -->
          <template v-if="selected.scope === 'article_type'">
            <el-divider>切入角度</el-divider>
            <el-alert
              type="info"
              :closable="false"
              show-icon
              style="margin-bottom: 10px"
            >
              <template #title>
                在上方「模板内容」中写入 <code>{entry_angle}</code>，生成时会从下列角度中<strong>随机选一条</strong>替换该占位符。<br/>
                未配置任何有效角度时，占位符将替换为空。话题池任务不注入切入角度。
              </template>
            </el-alert>
            <div v-if="!editAngles.length" class="angles-empty">
              <span>暂无切入角度，可点击下方新增。</span>
            </div>
            <div v-for="(ang, idx) in editAngles" :key="ang.id" class="angle-card">
              <div class="angle-card-head">
                <el-input
                  v-model="ang.label"
                  size="small"
                  placeholder="角度名称（可选，如：新手痛点）"
                  style="max-width: 240px"
                />
                <el-button
                  type="danger"
                  link
                  :icon="Delete"
                  @click="removeEntryAngle(idx)"
                >
                  删除
                </el-button>
              </div>
              <el-input
                v-model="ang.prompt"
                type="textarea"
                :rows="4"
                placeholder="该切入角度的具体提示词（必填才参与随机）"
              />
            </div>
            <div class="action-row angles-actions">
              <el-button :icon="Plus" @click="addEntryAngle">新增角度</el-button>
              <el-button type="primary" :icon="Check" @click="saveEntryAngles" :loading="savingAngles">
                保存切入角度
              </el-button>
              <el-tag v-if="selectedAnglesItem?.id" type="success" size="small">已保存</el-tag>
              <el-tag v-else type="info" size="small">未保存（保存后参与随机）</el-tag>
            </div>

            <el-divider>话题生成提示词</el-divider>
            <el-alert
              type="info"
              :closable="false"
              show-icon
              style="margin-bottom: 10px"
            >
              <template #title>
                此提示词用于「话题池」批量生成时让 LLM 产出话题词 + 搜索词。<br/>
                末尾须包含：<code>请生成 {n} 条，严格输出 JSON 数组：[{"topic_text":"...","search_query":"..."}, ...]</code>
              </template>
            </el-alert>
            <el-form label-width="90px" size="default">
              <el-form-item label="话题提示词">
                <el-input
                  v-model="editTopicForm.content"
                  type="textarea"
                  :rows="14"
                  placeholder="输入该类型的话题生成提示词，末尾加：请生成 {n} 条，严格输出 JSON 数组..."
                />
              </el-form-item>
              <el-form-item label="变更说明">
                <el-input
                  v-model="editTopicForm.change_note"
                  placeholder="可选"
                  maxlength="120"
                  show-word-limit
                />
              </el-form-item>
            </el-form>
            <div class="action-row">
              <el-button type="success" :icon="Check" @click="saveTopicEdit" :loading="savingTopic">
                {{ selectedTopicItem?.id ? '保存（创建新版本）' : '保存话题提示词' }}
              </el-button>
              <el-button
                v-if="selectedTopicItem?.id"
                :icon="Clock"
                @click="openTopicVersionDrawer"
              >
                历史版本
              </el-button>
              <el-button
                v-if="topicSource === 'overridden'"
                type="warning"
                :icon="RefreshLeft"
                @click="resetTopicToBuiltin"
              >
                恢复出厂
              </el-button>
              <el-button
                v-if="canDeleteTopic"
                type="danger"
                :icon="Delete"
                @click="deleteTopicCurrent"
              >
                {{ deleteTopicButtonText }}
              </el-button>
              <el-tag :type="sourceTagType(topicSource)" size="small">
                {{ sourceLabel(topicSource) }}
              </el-tag>
            </div>
          </template>
        </div>
      </el-card>

      <!-- 右栏：实时预览 -->
      <el-card class="col right" shadow="never">
        <template #header>
          <div class="col-header">
            <span>实时预览最终 Prompt</span>
            <el-button
              type="primary"
              size="small"
              :icon="View"
              @click="runPreview"
              :loading="previewing"
            >
              生成预览
            </el-button>
          </div>
        </template>

        <!-- 默认预览表单：全局 / 文章类型 -->
        <template v-if="activeScope === 'global' || activeScope === 'article_type'">
          <el-form label-width="80px" size="small" class="preview-form">
            <el-form-item label="文章类型">
              <el-select v-model="preview.article_type" placeholder="选择类型" filterable>
                <el-option
                  v-for="t in previewTypeOptions"
                  :key="t.key"
                  :label="`${t.key} ${t.name}`"
                  :value="t.key"
                />
              </el-select>
            </el-form-item>
          </el-form>

          <el-collapse class="mock-collapse">
            <el-collapse-item>
              <template #title>
                <span class="mock-title">
                  自定义 Mock 素材（可选，{{ preview.mock_materials?.length || 0 }} 条）
                </span>
              </template>
              <div class="mock-list">
                <div
                  v-for="(m, i) in preview.mock_materials"
                  :key="i"
                  class="mock-item"
                >
                  <el-input v-model="m.title" size="small" placeholder="素材标题" />
                  <el-input
                    v-model="m.content"
                    type="textarea"
                    size="small"
                    :rows="3"
                    placeholder="素材正文"
                  />
                  <el-button
                    size="small"
                    type="danger"
                    link
                    @click="removeMaterial(i)"
                  >移除</el-button>
                </div>
                <el-button size="small" :icon="Plus" @click="addMaterial">添加一条素材</el-button>
                <div class="hint">不填则使用默认示例素材。</div>
              </div>
            </el-collapse-item>
          </el-collapse>
        </template>

        <!-- 知乎问答预览表单（按选中 segment 自适应字段） -->
        <template v-else-if="activeScope === 'zhihu_qa'">
          <el-alert
            v-if="!selected"
            type="info"
            :closable="false"
            show-icon
            style="margin-bottom: 8px"
          >
            <template #title>请先在左栏选中要预览的片段（联网检索 / 拆回答方向 / 写正文）</template>
          </el-alert>
          <el-form
            v-if="selected"
            label-width="80px"
            size="small"
            class="preview-form"
          >
            <el-form-item label="知乎问题">
              <el-input
                v-model="zhihuPreview.question"
                type="textarea"
                :rows="2"
                placeholder="例：同人写手如何选择适合自己的发布平台？"
              />
            </el-form-item>
            <el-form-item
              v-if="selected.segment === 'direction_prompt' || selected.segment === 'article_prompt'"
              label="补充说明"
            >
              <el-input
                v-model="zhihuPreview.extra_hint"
                size="small"
                placeholder="可选：拆方向 / 写正文时的额外约束"
              />
            </el-form-item>
            <template v-if="selected.segment === 'article_prompt'">
              <el-form-item label="方向标题">
                <el-input v-model="zhihuPreview.direction_label" size="small" placeholder="如：新手痛点" />
              </el-form-item>
              <el-form-item label="方向内容">
                <el-input
                  v-model="zhihuPreview.direction_text"
                  type="textarea"
                  :rows="2"
                  placeholder="该方向的核心论点 / 立场 / 叙事主线"
                />
              </el-form-item>
              <el-form-item label="使用变体">
                <el-tag size="small" :type="(selected.scope_key || '') ? 'success' : 'info'">
                  {{ (selected.scope_key || '').trim() ? selected.scope_key : '默认（内置）' }}
                </el-tag>
                <span class="hint-text" style="margin-left: 8px">
                  自动跟随左栏当前选中的"写正文"变体
                </span>
              </el-form-item>
            </template>
            <el-form-item
              v-if="selected.segment === 'direction_prompt' || selected.segment === 'article_prompt'"
              label="检索素材"
            >
              <el-input
                v-model="zhihuPreview.sources_text"
                type="textarea"
                :rows="2"
                placeholder="可选：粘贴一段示例素材（默认空）"
              />
            </el-form-item>
          </el-form>
        </template>

        <!-- 专栏口播预览表单（按选中 segment 自适应字段） -->
        <template v-else-if="activeScope === 'column_oral'">
          <el-alert
            v-if="!selected"
            type="info"
            :closable="false"
            show-icon
            style="margin-bottom: 8px"
          >
            <template #title>请先在左栏选中要预览的片段（联网检索 / AI 拆方向 / 写口播稿）</template>
          </el-alert>
          <el-form
            v-if="selected"
            label-width="90px"
            size="small"
            class="preview-form"
          >
            <el-form-item label="主题">
              <el-input v-model="columnOralPreview.topic" size="small" placeholder="例：网红教育博主" />
            </el-form-item>
            <el-form-item
              v-if="selected.segment !== 'direction_prompt'"
              label="方向"
            >
              <el-input
                v-model="columnOralPreview.direction"
                type="textarea"
                :rows="2"
                placeholder="例：从陶行知「伪知识」理论分析其本质"
              />
            </el-form-item>
            <el-form-item label="理论资源">
              <el-input
                v-model="columnOralPreview.theory"
                size="small"
                placeholder="可选。例：本雅明 + 阿多诺"
              />
            </el-form-item>
            <el-form-item label="案例素材">
              <el-input
                v-model="columnOralPreview.cases"
                size="small"
                placeholder="可选。指定要分析的影视/广告/事件"
              />
            </el-form-item>
            <el-form-item
              v-if="selected.segment === 'article_prompt' || selected.segment === 'direction_prompt'"
              label="补充说明"
            >
              <el-input
                v-model="columnOralPreview.extra_hint"
                size="small"
                placeholder="可选。给写正文/拆方向的额外约束"
              />
            </el-form-item>
            <el-form-item
              v-if="selected.segment === 'article_prompt'"
              label="使用变体"
            >
              <el-tag size="small" :type="(selected.scope_key || '') ? 'success' : 'info'">
                {{ (selected.scope_key || '').trim() ? selected.scope_key : '默认（内置）' }}
              </el-tag>
              <span class="hint-text" style="margin-left: 8px">
                自动跟随左栏当前选中的"写口播稿"变体
              </span>
            </el-form-item>
            <el-form-item
              v-if="selected.segment === 'article_prompt'"
              label="检索素材"
            >
              <el-input
                v-model="columnOralPreview.sources_text"
                type="textarea"
                :rows="3"
                placeholder="可选：粘贴一段示例素材（默认空时使用占位文案）"
              />
            </el-form-item>
          </el-form>
        </template>

        <!-- Tabstory 预览表单（pill 风格） -->
        <template v-else-if="activeScope === 'tabstory'">
          <el-form label-width="80px" size="small" class="preview-form">
            <el-form-item label="模式">
              <el-radio-group v-model="tabstoryPreview.post_type" size="small">
                <el-radio-button value="A">指定问题</el-radio-button>
                <el-radio-button value="B">自问自答</el-radio-button>
              </el-radio-group>
            </el-form-item>
            <el-form-item v-if="tabstoryPreview.post_type === 'A'" label="原始问题">
              <el-input
                v-model="tabstoryPreview.question"
                type="textarea"
                :rows="2"
                placeholder="粘贴一个 Quora 问题作为预览输入"
              />
            </el-form-item>
            <el-form-item label="目标受众">
              <el-select v-model="tabstoryPreview.audience" size="small">
                <el-option
                  v-for="it in tabstoryOptions.audiences"
                  :key="it.value"
                  :label="it.label"
                  :value="it.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="差异角度">
              <el-select v-model="tabstoryPreview.angle" size="small">
                <el-option
                  v-for="it in tabstoryOptions.angles"
                  :key="it.value"
                  :label="it.label"
                  :value="it.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="语气风格">
              <el-select v-model="tabstoryPreview.tone" size="small">
                <el-option
                  v-for="it in tabstoryOptions.tones"
                  :key="it.value"
                  :label="it.label"
                  :value="it.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="输出语言">
              <el-select v-model="tabstoryPreview.language" size="small">
                <el-option
                  v-for="it in tabstoryOptions.languages"
                  :key="it.value"
                  :label="it.label"
                  :value="it.value"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="关键词">
              <div class="ts-preview-kw-group">
                <el-tag
                  v-for="kw in tabstoryOptions.keywords"
                  :key="kw.label"
                  :type="tabstoryPreview.keywords.includes(kw.label) ? 'primary' : 'info'"
                  :effect="tabstoryPreview.keywords.includes(kw.label) ? 'dark' : 'plain'"
                  size="small"
                  style="cursor: pointer; margin: 0 4px 4px 0"
                  @click="togglePreviewKeyword(kw.label)"
                >{{ kw.label }}</el-tag>
                <div v-if="!tabstoryOptions.keywords.length" class="hint-text">
                  关键词候选池为空。请在左栏「关键词候选池」修改后再来预览。
                </div>
              </div>
            </el-form-item>
          </el-form>
        </template>

        <!-- B站洗稿 预览表单 -->
        <template v-else-if="activeScope === 'bilibili_rewrite'">
          <el-form label-width="100px" size="small" class="preview-form">
            <el-form-item label="视频标题">
              <el-input v-model="biliRewritePreview.title" placeholder="例如：5 分钟讲清 React Server Components" />
            </el-form-item>
            <el-form-item label="UP 主">
              <el-input v-model="biliRewritePreview.uploader" placeholder="例如：尚硅谷-周老师" />
            </el-form-item>
            <el-form-item label="时长（秒）">
              <el-input-number v-model="biliRewritePreview.duration" :min="0" :step="60" />
            </el-form-item>
            <el-form-item label="视频 URL">
              <el-input v-model="biliRewritePreview.url" placeholder="https://www.bilibili.com/video/BV..." />
            </el-form-item>
            <el-form-item label="转写来源">
              <el-select v-model="biliRewritePreview.transcript_source" size="small">
                <el-option label="B站官方字幕" value="B站官方字幕" />
                <el-option label="B站自动字幕" value="B站自动字幕" />
                <el-option label="ASR 转写" value="ASR 转写" />
              </el-select>
            </el-form-item>
            <el-form-item label="转写稿（前几段）">
              <el-input
                v-model="biliRewritePreview.transcript"
                type="textarea"
                :rows="4"
                placeholder="粘贴一段视频原始转写稿用于预览（也可以为空，会以空文本展示模板结构）"
              />
            </el-form-item>
            <el-form-item label="附加风格">
              <el-input
                v-model="biliRewritePreview.style_hint"
                type="textarea"
                :rows="2"
                placeholder="可空。例如：偏知乎口吻，3 个二级小标题，文末加一段总结"
              />
            </el-form-item>
          </el-form>
        </template>

        <!-- 抖音洗稿 预览表单 -->
        <template v-else-if="activeScope === 'douyin_rewrite'">
          <el-form label-width="100px" size="small" class="preview-form">
            <el-form-item label="视频标题">
              <el-input v-model="douyinRewritePreview.title" placeholder="例如：消防员逆行瞬间看哭无数人" />
            </el-form-item>
            <el-form-item label="作者">
              <el-input v-model="douyinRewritePreview.uploader" placeholder="例如：每日说法" />
            </el-form-item>
            <el-form-item label="时长（秒）">
              <el-input-number v-model="douyinRewritePreview.duration" :min="0" :step="10" />
            </el-form-item>
            <el-form-item label="视频 URL">
              <el-input v-model="douyinRewritePreview.url" placeholder="https://www.douyin.com/video/..." />
            </el-form-item>
            <el-form-item label="转写来源">
              <el-select v-model="douyinRewritePreview.transcript_source" size="small">
                <el-option label="ASR 转写" value="ASR 转写" />
              </el-select>
            </el-form-item>
            <el-form-item label="转写稿（前几段）">
              <el-input
                v-model="douyinRewritePreview.transcript"
                type="textarea"
                :rows="4"
                placeholder="粘贴一段抖音视频原始转写稿用于预览"
              />
            </el-form-item>
            <el-form-item label="附加风格">
              <el-input
                v-model="douyinRewritePreview.style_hint"
                type="textarea"
                :rows="2"
                placeholder="可空。例如：偏资讯口吻，文末加一句正能量收尾"
              />
            </el-form-item>
          </el-form>
        </template>

        <!-- 洗稿 预览表单 -->
        <template v-else-if="activeScope === 'content_rewrite'">
          <el-form label-width="100px" size="small" class="preview-form">
            <el-form-item label="原文示例">
              <el-input
                v-model="rewritePreview.original_content"
                type="textarea"
                :rows="4"
                placeholder="粘贴一段待洗稿原文用于预览"
              />
            </el-form-item>
            <el-form-item label="洗稿强度">
              <el-radio-group v-model="rewritePreview.intensity">
                <el-radio value="light">轻度</el-radio>
                <el-radio value="medium">中度</el-radio>
                <el-radio value="deep">深度</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="篇幅">
              <el-radio-group v-model="rewritePreview.length">
                <el-radio value="keep">保持原样</el-radio>
                <el-radio value="shorter">精简</el-radio>
                <el-radio value="longer">扩写</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="关键词保留">
              <el-input v-model="rewritePreview.keywords" placeholder="可空，逗号分隔" />
            </el-form-item>
            <el-form-item label="补充要求">
              <el-input
                v-model="rewritePreview.custom_prompt"
                type="textarea"
                :rows="2"
                placeholder="可空"
              />
            </el-form-item>
            <el-form-item label="版本">
              <el-radio-group v-model="rewritePreview.version_index">
                <el-radio :value="1">版本 1</el-radio>
                <el-radio :value="2">版本 2</el-radio>
                <el-radio :value="3">版本 3</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-form>
        </template>

        <el-tabs v-model="previewTab" class="preview-tabs">
          <el-tab-pane label="System Prompt" name="system">
            <div class="preview-box">
              <el-button
                class="copy-btn"
                size="small"
                :icon="CopyDocument"
                @click="copyText(previewResult.system)"
                v-if="previewResult.system"
              >复制</el-button>
              <pre v-if="previewResult.system">{{ previewResult.system }}</pre>
              <div v-else class="preview-empty">点击"生成预览"查看最终 System Prompt</div>
            </div>
          </el-tab-pane>
          <el-tab-pane label="User Prompt" name="user">
            <div class="preview-box">
              <el-button
                class="copy-btn"
                size="small"
                :icon="CopyDocument"
                @click="copyText(previewResult.user)"
                v-if="previewResult.user"
              >复制</el-button>
              <pre v-if="previewResult.user">{{ previewResult.user }}</pre>
              <div v-else class="preview-empty">点击"生成预览"查看最终 User Prompt</div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </el-card>
    </div>
    </el-tab-pane>

    <!-- ══════════════ 分区 2：话题池 ══════════════ -->
    <el-tab-pane v-if="showTopicPane" label="话题池" name="topic_pool">
      <div class="dict-tab">
        <!-- 工具栏 -->
        <div class="tab-toolbar">
          <el-select
            v-model="tpFilter.article_type"
            placeholder="全部文章类型"
            clearable
            style="width: 180px"
            @change="loadTopicPool"
          >
            <el-option
              v-for="(v, k) in tpArticleTypes"
              :key="k"
              :label="`${k} ${v.name}`"
              :value="k"
            />
          </el-select>
          <el-select
            v-model="tpFilter.status"
            placeholder="全部状态"
            clearable
            style="width: 120px"
            @change="loadTopicPool"
          >
            <el-option label="未使用" value="unused" />
            <el-option label="已使用" value="used" />
            <el-option label="保留" value="reserved" />
          </el-select>
          <el-input-number
            v-model="tpFilter.days"
            :min="1"
            :max="365"
            placeholder="最近 N 天"
            style="width: 120px"
            :controls="false"
            @change="loadTopicPool"
          />
          <div class="toolbar-spacer" />
          <!-- 库存统计 -->
          <el-tag v-for="(v, k) in tpStats" :key="k" type="info" effect="plain" style="margin-right: 4px">
            {{ k }}: 未用 {{ v.unused }} / 总 {{ v.total }}
          </el-tag>
          <el-button :icon="Refresh" @click="loadTopicPool">刷新</el-button>
          <el-button type="success" @click="openTpGenerateDialog">批量生成话题</el-button>
          <el-button :icon="Plus" @click="openTpManualDialog">手动录入</el-button>
        </div>

        <!-- 话题列表 -->
        <el-table
          :data="tpItems"
          v-loading="tpLoading"
          size="small"
          stripe
          border
          style="width: 100%"
        >
          <el-table-column prop="id" label="ID" width="60" />
          <el-table-column label="文章类型" width="120">
            <template #default="{ row }">
              <el-tag size="small" type="primary">{{ row.article_type }}</el-tag>
              <span style="margin-left: 4px; font-size: 12px; color: #666">{{ tpArticleTypes[row.article_type]?.name || '' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="话题词" min-width="260" show-overflow-tooltip>
            <template #default="{ row }">
              <span class="topic-text">{{ row.topic_text }}</span>
            </template>
          </el-table-column>
          <el-table-column label="搜索词" min-width="160" show-overflow-tooltip>
            <template #default="{ row }">
              <code class="mono" style="font-size: 12px; color: #409eff">{{ row.search_query || '—' }}</code>
            </template>
          </el-table-column>
          <el-table-column label="素材" width="100" align="center">
            <template #default="{ row }">
              <el-tag
                size="small"
                :type="row.materials?.length ? 'success' : 'info'"
                class="tp-mat-trigger"
                @click="openTpMaterialsDialog(row)"
              >
                {{ row.materials?.length || 0 }} 条
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="90" align="center">
            <template #default="{ row }">
              <el-tag
                size="small"
                :type="row.status === 'unused' ? 'success' : row.status === 'used' ? 'info' : 'warning'"
              >{{ { unused: '未使用', used: '已使用', reserved: '保留' }[row.status] || row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="来源" width="75" align="center">
            <template #default="{ row }">
              <el-tag size="small" :type="row.source === 'manual' ? 'warning' : ''">
                {{ row.source === 'manual' ? '手动' : '自动' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="生成时间" width="140">
            <template #default="{ row }">
              <span style="font-size: 12px">{{ row.created_at?.slice(0, 16).replace('T', ' ') }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="{ row }">
              <el-button
                v-if="row.status === 'used'"
                link
                type="warning"
                size="small"
                @click="resetTpStatus(row)"
              >重置为未使用</el-button>
              <el-button
                v-if="row.status === 'unused'"
                link
                type="info"
                size="small"
                @click="reserveTpEntry(row)"
              >标为保留</el-button>
              <el-popconfirm
                :title="`确认删除话题 #${row.id}？`"
                confirm-button-type="danger"
                @confirm="deleteTpEntry(row)"
              >
                <template #reference>
                  <el-button link type="danger" size="small">删除</el-button>
                </template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-tab-pane>

    </el-tabs>

    <!-- ══════════════ 话题池：批量生成对话框 ══════════════ -->
    <el-dialog v-model="tpGenerateDialog.visible" title="批量生成话题词" width="620px">
      <el-form :model="tpGenerateDialog.form" label-width="120px">
        <el-form-item label="文章类型">
          <el-checkbox-group v-model="tpGenerateDialog.form.article_types">
            <el-checkbox v-for="(v, k) in tpArticleTypes" :key="k" :label="k">
              {{ k }} {{ v.name }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="IP / 作品锚点">
          <el-input
            v-model="tpGenerateDialog.form.theme_ip"
            type="textarea"
            :rows="2"
            maxlength="500"
            show-word-limit
            placeholder="可选。书名、角色、品牌、剧集等；海外市场请用英文描述以便模型理解。"
          />
          <span class="hint-text" style="display: block; margin-top: 4px">写入生成提示词，国内与海外共用此两项；留空则按各类型默认策略多样化。</span>
        </el-form-item>
        <el-form-item label="主题引导词">
          <el-input
            v-model="tpGenerateDialog.form.theme_guidance"
            type="textarea"
            :rows="2"
            maxlength="800"
            show-word-limit
            placeholder="可选。希望侧重的方向、口吻、禁忌或热点角度等。"
          />
        </el-form-item>
        <el-form-item label="生成总条数">
          <el-input-number v-model="tpGenerateDialog.form.total_count" :min="1" :max="100" />
          <span class="hint-text" style="margin-left: 8px">按选中类型平均分配</span>
        </el-form-item>
        <el-form-item label="搜索素材">
          <el-switch v-model="tpGenerateDialog.form.search_materials" />
          <span class="hint-text" style="margin-left: 8px">开启后每条话题自动 WebSearch 抓取素材</span>
        </el-form-item>
        <el-form-item label="素材预算" v-if="tpGenerateDialog.form.search_materials">
          <el-input-number v-model="tpGenerateDialog.form.material_budget" :min="1" :max="10" />
          <span class="hint-text" style="margin-left: 8px">每条话题最多搜几条素材</span>
        </el-form-item>
      </el-form>
      <!-- 进度显示 -->
      <div v-if="tpGenerateDialog.jobId" class="tp-progress-block">
        <el-progress
          :percentage="tpGenerateDialog.progressPct"
          :status="tpGenerateDialog.jobStatus === 'completed' ? 'success' : tpGenerateDialog.jobStatus === 'failed' ? 'exception' : undefined"
        />
        <p class="tp-progress-msg">{{ tpGenerateDialog.progressMsg }}</p>
      </div>
      <template #footer>
        <el-button @click="tpGenerateDialog.visible = false">关闭</el-button>
        <el-button
          type="primary"
          :loading="tpGenerateDialog.running"
          :disabled="!tpGenerateDialog.form.article_types.length"
          @click="submitTpGenerate"
        >开始生成</el-button>
      </template>
    </el-dialog>

    <!-- ══════════════ 话题池：手动录入对话框 ══════════════ -->
    <el-dialog v-model="tpManualDialog.visible" title="手动录入话题词" width="520px">
      <el-form :model="tpManualDialog.form" label-width="100px">
        <el-form-item label="文章类型" required>
          <el-select v-model="tpManualDialog.form.article_type" style="width: 100%">
            <el-option v-for="(v, k) in tpArticleTypes" :key="k" :label="`${k} ${v.name}`" :value="k" />
          </el-select>
        </el-form-item>
        <el-form-item label="话题词" required>
          <el-input
            v-model="tpManualDialog.form.topic_text"
            type="textarea"
            :rows="3"
            placeholder="输入具体的写作话题描述..."
          />
        </el-form-item>
        <el-form-item label="搜索词">
          <el-input
            v-model="tpManualDialog.form.search_query"
            placeholder="对应 WebSearch 搜索词（可选）"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="tpManualDialog.visible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="tpManualDialog.saving"
          :disabled="!tpManualDialog.form.article_type || !tpManualDialog.form.topic_text"
          @click="submitTpManual"
        >录入</el-button>
      </template>
    </el-dialog>

    <!-- ══════════════ 话题池：素材详情 ══════════════ -->
    <el-dialog
      v-model="tpMaterialsDialog.visible"
      title="话题素材详情"
      width="760px"
      destroy-on-close
    >
      <div class="tp-mat-meta">
        <el-tag size="small">{{ tpMaterialsDialog.articleType }}</el-tag>
        <span
          v-if="tpMaterialsDialog.searchQuery"
          class="mono tp-mat-search"
        >搜索词：{{ tpMaterialsDialog.searchQuery }}</span>
      </div>
      <p class="tp-mat-topic">{{ tpMaterialsDialog.topicText || '—' }}</p>
      <el-empty v-if="!tpMaterialsDialog.materials.length" description="暂无素材" />
      <el-scrollbar v-else max-height="480px">
        <div
          v-for="(m, idx) in tpMaterialsDialog.materials"
          :key="idx"
          class="tp-mat-card"
        >
          <div class="tp-mat-card-head">
            <strong>{{ m.title || `素材 ${idx + 1}` }}</strong>
            <el-tag v-if="m.source" size="small" type="info" effect="plain">{{ m.source }}</el-tag>
            <el-tag v-if="m.heat != null && Number(m.heat) !== 0" size="small" effect="plain">热度 {{ m.heat }}</el-tag>
            <el-tag v-if="m.type" size="small" effect="plain">{{ m.type }}</el-tag>
          </div>
          <div class="tp-mat-card-body">{{ m.content || '（无正文）' }}</div>
          <div v-if="m.keyword" class="hint-text tp-mat-kw">关联关键词：{{ m.keyword }}</div>
        </div>
      </el-scrollbar>
      <template #footer>
        <el-button @click="tpMaterialsDialog.visible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 文章类型专用新增对话框（新建类型） -->
    <el-dialog v-model="createTypeDialogVisible" title="新增文章类型" width="660px">
      <el-form :model="createTypeForm" label-width="110px">
        <el-form-item label="类型代码" required>
          <el-input
            v-model="createTypeForm.type_code"
            placeholder="如 T-20"
            style="width: 160px"
          />
        </el-form-item>
        <el-form-item label="类型名称" required>
          <el-input
            v-model="createTypeForm.type_name"
            placeholder="如 短视频脚本型"
            style="width: 260px"
          />
        </el-form-item>
        <el-form-item label="写作框架">
          <el-input
            v-model="createTypeForm.framework"
            type="textarea"
            :rows="8"
            placeholder="该类型文章的写作结构、写法要求、禁忌等（供正文生成 System Prompt 使用）"
          />
        </el-form-item>
        <el-divider>话题生成提示词</el-divider>
        <el-form-item label="话题生成提示词">
          <el-alert type="info" :closable="false" show-icon style="margin-bottom: 8px">
            <template #title>
              告诉 LLM 如何为该类型批量生成话题词 + 搜索词。<br/>
              末尾必须包含：<code>请生成 {n} 条，严格输出 JSON 数组：[{"topic_text":"...","search_query":"..."}, ...]</code>
            </template>
          </el-alert>
          <el-input
            v-model="createTypeForm.topic_prompt"
            type="textarea"
            :rows="8"
            placeholder="输入话题生成提示词..."
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createTypeDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creatingType" @click="submitCreateType">创建</el-button>
      </template>
    </el-dialog>

    <!-- 新增模板对话框（全局/组合维度用） -->
    <el-dialog
      v-model="createDialogVisible"
      title="新增提示词模板"
      width="600px"
    >
      <el-form :model="createForm" label-width="110px">
        <el-form-item label="维度">
          <el-select v-model="createForm.scope" placeholder="选择维度" @change="onCreateScopeChange">
            <el-option v-for="s in scopeOptions" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="维度键" v-if="createForm.scope !== 'global' && createForm.scope !== 'zhihu_qa' && createForm.scope !== 'column_oral'">
          <el-select
            v-model="createForm.scope_key"
            :placeholder="`选择${currentCreateScopeLabel}`"
            filterable
            :allow-create="createForm.scope === 'topic'"
          >
            <el-option
              v-for="opt in createScopeKeyOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <!-- 知乎问答 · 写正文 多变体名称 -->
        <el-form-item
          v-if="createForm.scope === 'zhihu_qa' && createForm.segment === 'article_prompt'"
          label="变体名称"
        >
          <el-input
            v-model="createForm.scope_key"
            placeholder="给这份「写正文 System」起个名字，如：严肃科普风"
            maxlength="40"
            show-word-limit
          />
          <div class="field-hint">
            将作为该变体的唯一标识，在知乎问答生成页可下拉选用。留空 = 修改"默认（内置）"那份。
          </div>
        </el-form-item>
        <!-- 专栏口播 · 写口播稿 多变体名称 -->
        <el-form-item
          v-if="createForm.scope === 'column_oral' && createForm.segment === 'article_prompt'"
          label="变体名称"
        >
          <el-input
            v-model="createForm.scope_key"
            placeholder="给这份「写口播稿 System」起个名字，如：严肃考据风"
            maxlength="40"
            show-word-limit
          />
          <div class="field-hint">
            将作为该变体的唯一标识，在专栏口播生成页可下拉选用。留空 = 修改"默认（内置）"那份。
          </div>
        </el-form-item>
        <el-form-item label="片段">
          <el-select
            v-model="createForm.segment"
            placeholder="选择片段"
            :disabled="createForm.scope === 'zhihu_qa' || createForm.scope === 'column_oral'"
          >
            <el-option
              v-for="seg in createSegmentOptions"
              :key="seg"
              :label="segmentLabel(seg)"
              :value="seg"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="createForm.title" placeholder="可选：给这条起个名字" />
        </el-form-item>
        <el-form-item label="模板内容">
          <el-alert
            v-if="createForm.segment === 'topic_prompt'"
            type="info"
            :closable="false"
            show-icon
            style="margin-bottom: 8px"
          >
            <template #title>
              话题生成提示词：告诉 LLM 如何为该文章类型生成话题词 + 搜索词。<br/>
              末尾必须包含：<code>请生成 {n} 条，严格输出 JSON 数组：[{"topic_text":"...","search_query":"..."}, ...]</code>
            </template>
          </el-alert>
          <el-input
            v-model="createForm.content"
            type="textarea"
            :rows="12"
            :placeholder="createForm.segment === 'topic_prompt'
              ? '输入该类型的话题生成提示词，末尾加：请生成 {n} 条，严格输出 JSON 数组...'
              : '输入模板正文，支持 {占位符}'"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="submitCreate">创建</el-button>
      </template>
    </el-dialog>

    <!-- 历史版本抽屉 -->
    <el-drawer
      v-model="versionDrawerVisible"
      :title="versionDrawerTarget === 'topic' ? '话题提示词 · 历史版本' : '历史版本'"
      size="780px"
    >
      <div v-loading="loadingVersions">
        <el-empty v-if="!versions.length" description="暂无版本记录" />
        <el-timeline v-else>
          <el-timeline-item
            v-for="v in versions"
            :key="v.id"
            :timestamp="formatTime(v.created_at)"
            :type="v.is_current ? 'primary' : 'info'"
          >
            <div class="version-head">
              <b>v{{ v.version_no }}</b>
              <el-tag v-if="v.is_current" type="success" size="small">当前版本</el-tag>
              <span class="version-note">{{ v.change_note || '无说明' }}</span>
              <el-button
                v-if="!v.is_current"
                link
                type="primary"
                @click="doRollback(v.version_no)"
              >
                回滚到此版本
              </el-button>
              <el-button
                v-if="!v.is_current"
                link
                @click="toggleDiff(v)"
              >
                {{ diffVersionId === v.id ? '隐藏对比' : '与当前对比' }}
              </el-button>
            </div>
            <div v-if="diffVersionId === v.id" class="diff-view">
              <div class="diff-title">与当前版本 (v{{ currentVersionNo }}) 的差异</div>
              <div class="diff-lines">
                <div
                  v-for="(line, idx) in renderDiff(v.content)"
                  :key="idx"
                  :class="'diff-' + line.type"
                >
                  <span class="diff-prefix">{{ prefixForType(line.type) }}</span>
                  <span class="diff-text">{{ line.text || ' ' }}</span>
                </div>
              </div>
            </div>
            <el-collapse v-else>
              <el-collapse-item :name="v.id">
                <template #title>查看内容</template>
                <pre class="version-content">{{ v.content }}</pre>
              </el-collapse-item>
            </el-collapse>
          </el-timeline-item>
        </el-timeline>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  Plus, Refresh, Check, Clock, RefreshLeft, Delete, View, CopyDocument, Search
} from '@element-plus/icons-vue'
import { promptApi, topicPoolApi, zhihuQaApi, tabstoryApi, bilibiliRewriteApi, douyinRewriteApi, rewriteApi, columnOralApi } from '../api'

// 维度显示名
const SCOPE_LABEL = {
  global: '全局',
  article_type: '文章类型',
  zhihu_qa: '知乎问答',
  column_oral: '专栏口播',
  tabstory: 'Tabstory',
  bilibili_rewrite: 'B站洗稿',
  douyin_rewrite: '抖音洗稿',
  content_rewrite: '洗稿',
}

// 片段显示名
const SEGMENT_LABEL = {
  role_intro: '角色引导',
  type_framework: '写作框架',
  entry_angles: '切入角度库',
  topic_prompt: '话题生成提示词',
  quality_redlines: '质量红线',
  huaben_features: '话本能力',
  json_schema: 'JSON 输出',
  user_template: 'User 模板',
  search_system_prompt: '联网检索（System）',
  direction_prompt: '拆回答方向',
  article_prompt: '写正文（System）',
  // Tabstory / B站洗稿
  audiences: '目标受众（选项库）',
  angles: '核心差异化角度（选项库）',
  keywords: '关键词候选池（选项库）',
  tones: '语气风格（选项库）',
  languages: '输出语言（选项库）',
  system_prompt: 'System Prompt',
}

// 每个 scope 允许的 segments
const SCOPE_SEGMENTS = {
  global: ['role_intro', 'quality_redlines', 'huaben_features', 'json_schema', 'user_template'],
  article_type: ['type_framework', 'entry_angles', 'topic_prompt'],
  zhihu_qa: ['search_system_prompt', 'direction_prompt', 'article_prompt'],
  column_oral: ['search_system_prompt', 'direction_prompt', 'article_prompt'],
  tabstory: ['audiences', 'angles', 'keywords', 'tones', 'languages', 'system_prompt', 'user_template'],
  bilibili_rewrite: ['system_prompt', 'user_template'],
  douyin_rewrite: ['system_prompt', 'user_template'],
  content_rewrite: ['system_prompt', 'user_template'],
}

const ZHIHU_QA_SEGMENT_HINTS = {
  search_system_prompt: '第①步联网检索 System（系统设置 · 知乎问答 · 联网检索）；每条素材 angle 紧扣该问题。User 模板固定，占位符 {keyword}。',
  direction_prompt: '拆方向步骤的 System；4–8 条「回答核心方向」；程序不截断模型输出，仅提示词引导写法。',
  article_prompt: '按方向写正文时的 System；全文须贯彻【本条回答核心方向】，不得答非所问。',
}

const COLUMN_ORAL_SEGMENT_HINTS = {
  search_system_prompt: '联网检索 System：按「主题 + 方向」聚文献原文/案例素材/思想家背景，每条带 source/url。User 模板固定，占位符 {topic}/{direction}/{theory_block}/{cases_block}/{keyword}。',
  direction_prompt: 'AI 拆方向 System：基于「主题（+ 可选理论/案例/补充说明）」一键拆 4-8 条候选批评方向（不联网，速度优先）。模型须输出 JSON 数组 [{id,label(≤12 字),direction(1-3 句)}]，每条须紧扣该主题且互相差异明显。',
  article_prompt: '写口播稿 System（默认对应 190 行文化批评提示词）。模型必须返回合法 JSON：title / content / tags / references；content 内 [ref:N] 标记会在文章详情自动渲染成可点击徽章。',
}

// Tabstory 各 segment 的编辑提示
const TABSTORY_SEGMENT_HINTS = {
  audiences:  'JSON 数组型选项库。格式：{"items":[{"value":"x","label":"显示名"},...]}。修改后立即在「Tabstory 发帖」页生效。',
  angles:     'JSON 数组型选项库。格式同上。',
  keywords:   'JSON 数组型关键词池。格式：{"items":[{"label":"text to manga"},...]}。',
  tones:      'JSON 数组型选项库。格式：{"items":[{"value":"x","label":"显示名"},...]}。',
  languages:  'JSON 数组型选项库。格式：{"items":[{"value":"en","label":"🇺🇸 英文"},...]}。',
  system_prompt: 'Tabstory 写帖 System。须保留双语输出协议：--- CHINESE REFERENCE --- 与 --- KEYWORDS --- 两个分隔标记。',
  user_template: 'Tabstory User 模板。可用占位符：{mode_block}/{audience_label}/{angle_label}/{keywords_block}/{tone_label}/{language_label}。',
}

// B站洗稿各 segment 的编辑提示
const BILI_REWRITE_SEGMENT_HINTS = {
  system_prompt: 'B站洗稿 System。默认不用任何占位符。修改后立即在「B站洗稿」任务中生效。',
  user_template: 'B站洗稿 User 模板。默认使用占位符：{transcript_source}/{transcript}/{style_hint_block}（其中 {style_hint_block} 仅在填了"附加风格要求"时展开）。如需在 prompt 中带入视频元信息，可手动加入 {title}/{uploader}/{duration_min}/{url}/{style_hint} 占位符，业务侧仍会替换。',
}

// 抖音洗稿各 segment 的编辑提示
const DOUYIN_REWRITE_SEGMENT_HINTS = {
  system_prompt: '抖音洗稿 System。默认不用任何占位符。修改后立即在「抖音洗稿」任务中生效，与 B 站洗稿互不影响。',
  user_template: '抖音洗稿 User 模板。默认使用占位符：{transcript_source}/{transcript}/{style_hint_block}。可选手动加入 {title}/{uploader}/{duration_min}/{url}/{style_hint}。',
}

const CONTENT_REWRITE_SEGMENT_HINTS = {
  system_prompt: '通用洗稿 System。定义「本质不变、表达要变」等硬性规则。修改后立即在「洗稿」页生效。',
  user_template: '洗稿 User 模板。占位符：{original_content}（原文）、{intensity_desc}（强度）、{length_desc}（篇幅）、{keywords_block}（关键词保留，可空）、{custom_block}（补充要求，可空）、{version_hint}（本版本风格，用于 3 个版本差异化）。',
}

// 各 segment 可用的占位符（给用户提示）
const SEGMENT_PLACEHOLDERS = {
  platform_rules: ['min_length', 'max_length', 'content_style', 'title_style', 'opening_requirement', 'huaben_insertion'],
  type_framework: ['entry_angle'],
  user_template: ['type_name', 'n_materials', 'sources_text', 'topic_name', 'platform_name', 'writing_framework', 'insert_strategy', 'insert_intensity'],
}

// 关键占位符：删除后会导致最终 Prompt 严重缺信息，保存时警告用户
const CRITICAL_PLACEHOLDERS = {
  user_template: ['sources_text', 'topic_name'],
  platform_rules: [],
}

function extractPlaceholders(text) {
  const re = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g
  const found = new Set()
  let m
  while ((m = re.exec(text)) !== null) found.add(m[1])
  return Array.from(found)
}

export default {
  name: 'Prompts',
  props: {
    /** templates | topic_pool | all */
    mode: { type: String, default: 'templates' },
  },
  setup(props) {
    const activeScope = ref('global')
    const search = ref('')
    const loadingList = ref(false)
    const items = ref([])
    const selected = ref(null)
    const saving = ref(false)

    const meta = ref({ platforms: [], article_types: [], article_types_overseas: [], segments: [], scopes: [] })

    const editForm = reactive({
      title: '',
      description: '',
      content: '',
      change_note: '',
    })

    // ── 预览相关 ──
    const preview = reactive({
      platform: 'xiaohongshu',
      article_type: 'T-13',
      angle_seed: 0,
      mock_materials: [],
    })

    const addMaterial = () => {
      preview.mock_materials.push({ title: '', content: '', heat: 0 })
    }
    const removeMaterial = (i) => {
      preview.mock_materials.splice(i, 1)
    }
    const previewResult = ref({ system: '', user: '' })
    const previewTab = ref('system')
    const previewing = ref(false)

    // ── 知乎问答 / Tabstory 预览参数 ──
    const zhihuPreview = reactive({
      question: '同人写手如何选择适合自己的发布平台？',
      extra_hint: '',
      direction_label: '新手痛点',
      direction_text: '从新手痛点切入，给出可执行的对比与选择建议。',
      sources_text: '',
      article_prompt_key: '',
    })

    const columnOralPreview = reactive({
      topic: '网红教育博主',
      direction: '从陶行知「伪知识」理论分析其本质',
      theory: '',
      cases: '',
      extra_hint: '',
      sources_text: '',
    })

    const tabstoryPreview = reactive({
      post_type: 'A',
      audience: 'novel_author',
      angle: 'long_text',
      tone: 'firsthand',
      language: 'en',
      keywords: ['text to manga', 'no drawing skills'],
      question: "What's the best AI tool to convert a novel into manga without drawing skills?",
    })

    const tabstoryOptions = reactive({
      audiences: [],
      angles: [],
      keywords: [],
      tones: [],
      languages: [],
    })

    const biliRewritePreview = reactive({
      title: '5 分钟讲清楚 React Server Components',
      uploader: '前端早早聊',
      duration: 320,
      url: 'https://www.bilibili.com/video/BV1xxxxxxxxx',
      transcript_source: 'B站官方字幕',
      transcript: '今天咱们聊聊 RSC，也就是 React Server Components。说实话我一开始也没搞明白，后来看了官方文档配合一些源码才整明白……',
      style_hint: '',
    })

    const douyinRewritePreview = reactive({
      title: '一边激流救抱树男孩，一边鏖战12小时席地而睡',
      uploader: '每日说法',
      duration: 28,
      url: 'https://www.douyin.com/video/7660018402437631268',
      transcript_source: 'ASR 转写',
      transcript: '广西遭遇洪水，消防员一边激流救人，一边连续作战十二小时……',
      style_hint: '',
    })

    const rewritePreview = reactive({
      original_content: '这是一段待洗稿的示例原文。核心观点是：内容创作需要持续输出，但更重要的是保持观点的一致性与可读性。',
      intensity: 'medium',
      length: 'keep',
      keywords: '',
      custom_prompt: '',
      version_index: 1,
    })

    const loadTabstoryPreviewOptions = async () => {
      try {
        const opts = await tabstoryApi.getOptions()
        tabstoryOptions.audiences = opts.audiences || []
        tabstoryOptions.angles    = opts.angles    || []
        tabstoryOptions.keywords  = opts.keywords  || []
        tabstoryOptions.tones     = opts.tones     || []
        tabstoryOptions.languages = opts.languages || []
        // 默认选第一项（如果当前选中的不在列表里）
        const ensure = (key, listKey, fallback) => {
          if (!tabstoryOptions[listKey].some(it => (it.value || it.label) === tabstoryPreview[key])) {
            const first = tabstoryOptions[listKey][0]
            if (first) tabstoryPreview[key] = first.value || first.label || fallback
          }
        }
        ensure('audience', 'audiences', '')
        ensure('angle', 'angles', '')
        ensure('tone', 'tones', '')
        ensure('language', 'languages', 'en')
      } catch (e) {
        // 静默：选项不在时用户也能看到默认 pill
      }
    }

    const togglePreviewKeyword = (label) => {
      const i = tabstoryPreview.keywords.indexOf(label)
      if (i >= 0) tabstoryPreview.keywords.splice(i, 1)
      else tabstoryPreview.keywords.push(label)
    }

    // ── 话题提示词（文章类型选中时加载） ──
    const selectedTopicItem = ref(null)
    const editTopicForm = reactive({ content: '', change_note: '' })
    const savingTopic = ref(false)
    // 切入角度（entry_angles 片段，JSON 存库）
    const editAngles = ref([])
    const selectedAnglesItem = ref(null)
    const savingAngles = ref(false)
    const makeAngleId = () => `a_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    // 内置话题提示词（从后端读，用于显示默认内容）
    const builtinTopicPrompts = ref({})

    const loadBuiltinTopicPrompts = async () => {
      try {
        const res = await import('axios').then(a => a.default.get('/api/topic-pool/builtin-prompts'))
        builtinTopicPrompts.value = res.data || {}
      } catch (e) { /* 静默，兜底显示空 */ }
    }

    const refreshEntryAnglesSelection = () => {
      if (!selected.value || selected.value.scope !== 'article_type') {
        editAngles.value = []
        selectedAnglesItem.value = null
        return
      }
      const anglesItem = items.value.find(
        x => x.scope === 'article_type'
          && x.scope_key === selected.value.scope_key
          && x.segment === 'entry_angles'
      )
      selectedAnglesItem.value = anglesItem || null
      if (anglesItem?.content) {
        try {
          const data = JSON.parse(anglesItem.content)
          editAngles.value = (data.angles || []).map((a, i) => ({
            id: a.id || makeAngleId(),
            label: a.label || '',
            prompt: a.prompt || '',
          }))
        } catch (e) {
          editAngles.value = []
        }
      } else {
        editAngles.value = []
      }
    }

    const addEntryAngle = () => {
      editAngles.value.push({ id: makeAngleId(), label: '', prompt: '' })
    }

    const removeEntryAngle = (idx) => {
      editAngles.value.splice(idx, 1)
    }

    const saveEntryAngles = async () => {
      if (!selected.value || selected.value.scope !== 'article_type') return
      savingAngles.value = true
      try {
        const content = JSON.stringify({
          angles: editAngles.value.map(a => ({
            id: a.id,
            label: (a.label || '').trim(),
            prompt: (a.prompt || '').trim(),
          })),
        })
        if (selectedAnglesItem.value?.id) {
          await promptApi.update(selectedAnglesItem.value.id, {
            content,
            change_note: '更新切入角度',
          })
          ElMessage.success('切入角度已保存')
        } else {
          await promptApi.create({
            scope: 'article_type',
            scope_key: selected.value.scope_key,
            segment: 'entry_angles',
            content,
            title: `${selected.value.scope_key} 切入角度`,
            change_note: '创建切入角度库',
          })
          ElMessage.success('切入角度已保存')
        }
        await loadList()
        refreshEntryAnglesSelection()
      } catch (e) {
        ElMessage.error('保存失败: ' + (e.message || ''))
      } finally {
        savingAngles.value = false
      }
    }

    const refreshTopicSelection = () => {
      if (!selected.value || selected.value.scope !== 'article_type') {
        selectedTopicItem.value = null
        editTopicForm.content = ''
        return
      }
      const topicItem = items.value.find(
        x => x.scope === 'article_type'
          && x.scope_key === selected.value.scope_key
          && x.segment === 'topic_prompt'
      )
      selectedTopicItem.value = topicItem || null
      editTopicForm.content = topicItem?.content
        || builtinTopicPrompts.value[selected.value.scope_key]
        || ''
    }

    const saveTopicEdit = async () => {
      if (!selected.value || selected.value.scope !== 'article_type') return
      if (!editTopicForm.content.trim()) {
        ElMessage.warning('话题生成提示词不能为空')
        return
      }
      savingTopic.value = true
      try {
        if (selectedTopicItem.value?.id) {
          await promptApi.update(selectedTopicItem.value.id, {
            content: editTopicForm.content,
            change_note: editTopicForm.change_note || null,
          })
          ElMessage.success('保存成功（已创建新版本）')
        } else {
          await promptApi.create({
            scope: 'article_type',
            scope_key: selected.value.scope_key,
            segment: 'topic_prompt',
            content: editTopicForm.content,
            title: `${selected.value.scope_key} 话题生成提示词`,
            change_note: editTopicForm.change_note || '从内置派生',
          })
          ElMessage.success('已创建自定义覆盖')
        }
        editTopicForm.change_note = ''
        await loadList()
        refreshTopicSelection()
      } catch (e) {
        ElMessage.error('保存失败: ' + (e.message || ''))
      } finally {
        savingTopic.value = false
      }
    }

    // ── 新增模板对话框 ──
    const createDialogVisible = ref(false)
    const createForm = reactive({
      scope: 'global',
      scope_key: '',
      segment: '',
      title: '',
      content: '',
    })
    const creating = ref(false)

    // ── 文章类型专用新增对话框 ──
    const createTypeDialogVisible = ref(false)
    const createTypeForm = reactive({
      type_code: '',    // 如 T-20
      type_name: '',    // 如 短视频脚本型
      framework: '',    // 写作框架内容
      topic_prompt: '', // 话题生成提示词
    })
    const creatingType = ref(false)

    // ── 版本抽屉 ──
    const versionDrawerVisible = ref(false)
    const versionDrawerTarget = ref('framework') // framework | topic
    const versions = ref([])
    const loadingVersions = ref(false)
    const diffVersionId = ref(null)

    const scopeOptions = Object.entries(SCOPE_LABEL).map(([value, label]) => ({ value, label }))

    const currentScopeLabel = computed(() => SCOPE_LABEL[activeScope.value] || activeScope.value)

    const filteredItems = computed(() => {
      const kw = search.value.trim().toLowerCase()
      let list = items.value

      // article_type 视图：按 scope_key 去重，每个类型只显示一行
      if (activeScope.value === 'article_type') {
        const typeMap = new Map()
        for (const it of list) {
          // 优先保留 type_framework 作为代表项
          if (!typeMap.has(it.scope_key) || it.segment === 'type_framework') {
            typeMap.set(it.scope_key, it)
          }
        }
        list = Array.from(typeMap.values())
      }

      if (!kw) return list
      return list.filter(it => {
        const key = (it.scope_key || '').toLowerCase()
        const title = (it.title || '').toLowerCase()
        return key.includes(kw) || title.includes(kw)
      })
    })

    const availablePlaceholders = computed(() => {
      if (!selected.value) return []
      return SEGMENT_PLACEHOLDERS[selected.value.segment] || []
    })
    const deletableScopes = ['global', 'article_type', 'zhihu_qa', 'column_oral', 'tabstory', 'bilibili_rewrite', 'douyin_rewrite', 'content_rewrite']
    const canDeleteCurrent = computed(() => {
      if (!selected.value?.id) return false
      if (!deletableScopes.includes(selected.value.scope)) return false
      return selected.value.source === 'custom' || selected.value.source === 'overridden'
    })
    const deleteButtonText = computed(() => {
      if (!selected.value) return '删除'
      return selected.value.source === 'overridden' ? '删除覆盖' : '删除'
    })

    const topicSource = computed(() => {
      if (selectedTopicItem.value?.id) {
        return selectedTopicItem.value.source || 'custom'
      }
      if (selected.value?.scope_key && builtinTopicPrompts.value[selected.value.scope_key]) {
        return 'builtin'
      }
      return 'builtin'
    })

    const canDeleteTopic = computed(() => {
      if (!selectedTopicItem.value?.id) return false
      const src = selectedTopicItem.value.source
      return src === 'custom' || src === 'overridden'
    })

    const deleteTopicButtonText = computed(() => {
      return selectedTopicItem.value?.source === 'overridden' ? '删除覆盖' : '删除'
    })

    const versionDrawerItem = computed(() => {
      return versionDrawerTarget.value === 'topic' ? selectedTopicItem.value : selected.value
    })

    const currentCreateScopeLabel = computed(() => SCOPE_LABEL[createForm.scope] || '')

    const createScopeKeyOptions = computed(() => {
      if (createForm.scope === 'article_type') {
        return meta.value.article_types.map(t => ({ value: t.key, label: `${t.key} ${t.name}` }))
      }
      return []
    })

    const createSegmentOptions = computed(() => SCOPE_SEGMENTS[createForm.scope] || [])

    const segmentLabel = (seg) => SEGMENT_LABEL[seg] || seg

    const itemKey = (it) => `${it.scope}|${it.scope_key}|${it.segment}`
    const displayKey = (it) => {
      if (it.scope === 'global' && it.scope_key === 'overseas') return '全局（海外）'
      if (it.scope === 'global') return '全局'
      const kv = it.scope_key || '-'
      if (it.scope === 'article_type') {
        const merged = [...(meta.value.article_types || []), ...(meta.value.article_types_overseas || [])]
        const t = merged.find(x => x.key === kv)
        return t ? `${t.key} ${t.name}` : kv
      }
      if (it.scope === 'zhihu_qa') {
        const base = segmentLabel(it.segment)
        if (it.segment === 'article_prompt') {
          const variant = (it.scope_key || '').trim()
          if (!variant) {
            return `${base}（默认）`
          }
          const niceName = (it.title || '').trim() || variant
          return `${base} · ${niceName}`
        }
        return base
      }
      if (it.scope === 'column_oral') {
        const base = segmentLabel(it.segment)
        if (it.segment === 'article_prompt') {
          const variant = (it.scope_key || '').trim()
          if (!variant) {
            return `${base}（默认）`
          }
          const niceName = (it.title || '').trim() || variant
          return `${base} · ${niceName}`
        }
        return base
      }
      if (it.scope === 'tabstory') {
        return segmentLabel(it.segment)
      }
      if (it.scope === 'bilibili_rewrite') {
        return segmentLabel(it.segment)
      }
      if (it.scope === 'douyin_rewrite') {
        return segmentLabel(it.segment)
      }
      if (it.scope === 'content_rewrite') {
        return segmentLabel(it.segment)
      }
      return kv
    }

    const sourceLabel = (s) => ({ builtin: '内置', custom: '自定义', overridden: '已修改' })[s] || s
    const sourceTagType = (s) => ({ builtin: 'info', custom: 'success', overridden: 'warning' })[s] || 'info'

    const formatTime = (iso) => {
      if (!iso) return ''
      try {
        const d = new Date(iso)
        return d.toLocaleString('zh-CN')
      } catch (e) { return iso }
    }

    // ── 加载数据 ──
    const loadMeta = async () => {
      try {
        meta.value = await promptApi.getMeta()
      } catch (e) {
        ElMessage.error('加载维度枚举失败: ' + (e.message || ''))
      }
    }

    const loadList = async () => {
      loadingList.value = true
      try {
        const scope = activeScope.value
        const res = await promptApi.getList({ scope, market: contentMarket.value })
        items.value = res.items || []
        // 若当前选中项还在，刷新其状态；否则清空
        if (selected.value) {
          const same = items.value.find(it => itemKey(it) === itemKey(selected.value))
          if (same) {
            selected.value = same
            hydrateEditForm(same)
            refreshTopicSelection()
            refreshEntryAnglesSelection()
          } else {
            selected.value = null
            selectedTopicItem.value = null
          }
        }
      } catch (e) {
        ElMessage.error('加载列表失败: ' + (e.message || ''))
      } finally {
        loadingList.value = false
      }
    }

    const hydrateEditForm = (it) => {
      editForm.title = it.title || ''
      editForm.description = it.description || ''
      editForm.content = it.content || ''
      editForm.change_note = ''
    }

    const selectItem = (it) => {
      selected.value = it
      hydrateEditForm(it)
      editTopicForm.change_note = ''
      if (it.scope === 'article_type') {
        refreshTopicSelection()
        refreshEntryAnglesSelection()
      } else {
        selectedTopicItem.value = null
        editTopicForm.content = ''
        editAngles.value = []
        selectedAnglesItem.value = null
      }
    }

    const onScopeChange = () => {
      selected.value = null
      previewResult.value = { system: '', user: '' }
      loadList()
      if (activeScope.value === 'tabstory') {
        loadTabstoryPreviewOptions()
      }
    }

    // ── 保存 ──
    const validatePlaceholders = (segment, content) => {
      const allowed = SEGMENT_PLACEHOLDERS[segment] || []
      if (!allowed.length) return { ok: true }
      const used = extractPlaceholders(content)
      const unknown = used.filter(p => !allowed.includes(p))
      const missingCritical = (CRITICAL_PLACEHOLDERS[segment] || [])
        .filter(p => !used.includes(p))
      return { ok: !unknown.length && !missingCritical.length, unknown, missingCritical }
    }

    const saveEdit = async () => {
      if (!selected.value) return
      if (!editForm.content.trim()) {
        ElMessage.warning('模板内容不能为空')
        return
      }
      // 占位符校验
      const v = validatePlaceholders(selected.value.segment, editForm.content)
      if (!v.ok) {
        const msgs = []
        if (v.missingCritical?.length) {
          msgs.push(`关键占位符缺失：${v.missingCritical.map(p => '{' + p + '}').join(' / ')}`)
        }
        if (v.unknown?.length) {
          msgs.push(`未知占位符：${v.unknown.map(p => '{' + p + '}').join(' / ')}（将被原样保留，不会被替换）`)
        }
        try {
          await ElMessageBox.confirm(msgs.join('\n') + '\n\n仍然保存？', '占位符检查', {
            type: 'warning',
            confirmButtonText: '继续保存',
            cancelButtonText: '返回修改',
          })
        } catch (e) {
          return
        }
      }
      saving.value = true
      try {
        if (selected.value.source === 'builtin' || !selected.value.id) {
          // 内置：创建一条覆盖
          await promptApi.create({
            scope: selected.value.scope,
            scope_key: selected.value.scope_key,
            segment: selected.value.segment,
            content: editForm.content,
            title: editForm.title || null,
            description: editForm.description || null,
            change_note: editForm.change_note || '从内置派生',
          })
          ElMessage.success('已创建自定义覆盖')
        } else {
          await promptApi.update(selected.value.id, {
            content: editForm.content,
            title: editForm.title,
            description: editForm.description,
            change_note: editForm.change_note || null,
          })
          ElMessage.success('保存成功（已创建新版本）')
        }
        await loadList()
      } catch (e) {
        ElMessage.error('保存失败: ' + (e.message || ''))
      } finally {
        saving.value = false
      }
    }

    const resetToBuiltin = async () => {
      if (!selected.value?.id) return
      try {
        await ElMessageBox.confirm(
          '将恢复该条目到内置出厂默认，自定义内容与历史版本将被删除。继续？',
          '恢复出厂',
          { type: 'warning' }
        )
        await promptApi.resetToBuiltin(selected.value.id)
        ElMessage.success('已恢复到内置默认')
        await loadList()
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('恢复失败: ' + (e.message || ''))
      }
    }

    const deleteCurrent = async () => {
      if (!selected.value?.id) return
      const isOverride = selected.value.source === 'overridden'
      // 知乎问答 / 专栏口播 · 写正文 自定义变体：物理删除，避免后续重名冲突
      const isZhihuArticleVariant =
        selected.value.scope === 'zhihu_qa'
        && selected.value.segment === 'article_prompt'
        && (selected.value.scope_key || '').trim() !== ''
      const isColumnOralArticleVariant =
        selected.value.scope === 'column_oral'
        && selected.value.segment === 'article_prompt'
        && (selected.value.scope_key || '').trim() !== ''
      const useHard = isOverride || isZhihuArticleVariant || isColumnOralArticleVariant
      try {
        let msg = '确定删除此模板？（软删除）'
        if (isOverride) {
          msg = '确定删除这条覆盖模板吗？删除后会回到内置默认，并清除该条历史版本。'
        } else if (isZhihuArticleVariant) {
          msg = '确定删除这份"写正文"变体吗？删除后将无法在生成页选用，且会清除其历史版本。'
        } else if (isColumnOralArticleVariant) {
          msg = '确定删除这份"写口播稿"变体吗？删除后将无法在生成页选用，且会清除其历史版本。'
        }
        await ElMessageBox.confirm(msg, '删除', { type: 'warning' })
        await promptApi.delete(selected.value.id, useHard)
        ElMessage.success('已删除')
        selected.value = null
        await loadList()
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('删除失败: ' + (e.message || ''))
      }
    }

    // ── 新增 ──
    const openCreateDialog = () => {
      if (activeScope.value === 'article_type') {
        createTypeForm.type_code = ''
        createTypeForm.type_name = ''
        createTypeForm.framework = ''
        createTypeForm.topic_prompt = ''
        createTypeDialogVisible.value = true
        return
      }
      if (activeScope.value === 'tabstory') {
        ElMessage.info('Tabstory 各片段均已内置，直接在左栏点击对应条目即可修改并自动创建覆盖')
        return
      }
      if (activeScope.value === 'bilibili_rewrite') {
        ElMessage.info('B站洗稿各片段均已内置，直接在左栏点击对应条目即可修改并自动创建覆盖')
        return
      }
      if (activeScope.value === 'douyin_rewrite') {
        ElMessage.info('抖音洗稿各片段均已内置，直接在左栏点击对应条目即可修改并自动创建覆盖')
        return
      }
      if (activeScope.value === 'content_rewrite') {
        ElMessage.info('洗稿各片段均已内置，直接在左栏点击对应条目即可修改并自动创建覆盖')
        return
      }
      createForm.scope = activeScope.value === 'global' ? 'global' : activeScope.value
      createForm.scope_key = ''
      // 知乎问答 / 专栏口播：只允许新增"写正文 System"变体
      createForm.segment = (createForm.scope === 'zhihu_qa' || createForm.scope === 'column_oral')
        ? 'article_prompt'
        : ((SCOPE_SEGMENTS[createForm.scope] || [])[0] || '')
      createForm.title = ''
      createForm.content = ''
      createDialogVisible.value = true
    }

    const onCreateScopeChange = () => {
      createForm.scope_key = ''
      createForm.segment = (createForm.scope === 'zhihu_qa' || createForm.scope === 'column_oral')
        ? 'article_prompt'
        : ((SCOPE_SEGMENTS[createForm.scope] || [])[0] || '')
    }

    // ── 文章类型专用新增提交 ──
    const submitCreateType = async () => {
      const code = createTypeForm.type_code.trim().toUpperCase()
      const name = createTypeForm.type_name.trim()
      if (!code) { ElMessage.warning('请输入类型代码（如 T-20）'); return }
      if (!name) { ElMessage.warning('请输入类型名称'); return }
      if (!createTypeForm.framework.trim() && !createTypeForm.topic_prompt.trim()) {
        ElMessage.warning('写作框架和话题生成提示词至少填写一项')
        return
      }
      creatingType.value = true
      try {
        const tasks = []
        if (createTypeForm.framework.trim()) {
          tasks.push(promptApi.create({
            scope: 'article_type',
            scope_key: code,
            segment: 'type_framework',
            content: createTypeForm.framework,
            title: `${code} ${name} · 写作框架`,
          }))
        }
        if (createTypeForm.topic_prompt.trim()) {
          tasks.push(promptApi.create({
            scope: 'article_type',
            scope_key: code,
            segment: 'topic_prompt',
            content: createTypeForm.topic_prompt,
            title: `${code} ${name} · 话题生成提示词`,
          }))
        }
        await Promise.all(tasks)
        ElMessage.success(`${code} ${name} 创建成功`)
        createTypeDialogVisible.value = false
        activeScope.value = 'article_type'
        await loadList()
        // 刷新文章类型列表（供话题池等处同步）
        loadTpArticleTypes()
      } catch (e) {
        ElMessage.error('创建失败: ' + (e.message || ''))
      } finally {
        creatingType.value = false
      }
    }

    const submitCreate = async () => {
      const isZhihuArticle = createForm.scope === 'zhihu_qa' && createForm.segment === 'article_prompt'
      const isColumnOralArticle = createForm.scope === 'column_oral' && createForm.segment === 'article_prompt'
      const isMultiVariantArticle = isZhihuArticle || isColumnOralArticle
      if (
        createForm.scope !== 'global'
        && createForm.scope !== 'zhihu_qa'
        && createForm.scope !== 'column_oral'
        && !createForm.scope_key
      ) {
        ElMessage.warning('请选择维度键')
        return
      }
      if (!createForm.segment) {
        ElMessage.warning('请选择片段')
        return
      }
      if (!createForm.content.trim()) {
        ElMessage.warning('模板内容不能为空')
        return
      }
      // 多变体 article_prompt（知乎问答 / 专栏口播）：变体名称（即 scope_key）必须填
      if (isMultiVariantArticle) {
        const variantKey = (createForm.scope_key || '').trim()
        if (!variantKey) {
          ElMessage.warning('请填写变体名称（用于区分多份"写正文"模板）')
          return
        }
        if (variantKey.length > 40) {
          ElMessage.warning('变体名称不能超过 40 个字符')
          return
        }
      }
      creating.value = true
      try {
        // scope_key 落库规则：
        //   - global / 知乎问答 / 专栏口播 的非 article_prompt 片段 → 强制空
        //   - 知乎问答 / 专栏口播 的 article_prompt → 用 createForm.scope_key（变体名）
        //   - 其它 → createForm.scope_key
        let effectiveScopeKey = createForm.scope_key
        if (createForm.scope === 'global') {
          effectiveScopeKey = ''
        } else if (
          (createForm.scope === 'zhihu_qa' || createForm.scope === 'column_oral')
          && createForm.segment !== 'article_prompt'
        ) {
          effectiveScopeKey = ''
        } else if (isMultiVariantArticle) {
          effectiveScopeKey = (createForm.scope_key || '').trim()
        }
        await promptApi.create({
          scope: createForm.scope,
          scope_key: effectiveScopeKey,
          segment: createForm.segment,
          content: createForm.content,
          title: createForm.title || (isMultiVariantArticle ? effectiveScopeKey : null),
        })
        ElMessage.success('创建成功')
        createDialogVisible.value = false
        activeScope.value = createForm.scope
        await loadList()
      } catch (e) {
        ElMessage.error('创建失败: ' + (e.message || ''))
      } finally {
        creating.value = false
      }
    }

    const resetTopicToBuiltin = async () => {
      if (!selectedTopicItem.value?.id) return
      try {
        await ElMessageBox.confirm(
          '将恢复该类型的话题提示词到内置出厂默认，自定义内容与历史版本将被删除。继续？',
          '恢复出厂',
          { type: 'warning' }
        )
        await promptApi.resetToBuiltin(selectedTopicItem.value.id)
        ElMessage.success('已恢复到内置默认')
        await loadList()
        refreshTopicSelection()
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('恢复失败: ' + (e.message || ''))
      }
    }

    const deleteTopicCurrent = async () => {
      if (!selectedTopicItem.value?.id) return
      const isOverride = selectedTopicItem.value.source === 'overridden'
      try {
        const msg = isOverride
          ? '确定删除这条话题提示词覆盖吗？删除后会回到内置默认，并清除该条历史版本。'
          : '确定删除此话题提示词？（软删除）'
        await ElMessageBox.confirm(msg, '删除', { type: 'warning' })
        await promptApi.delete(selectedTopicItem.value.id, isOverride)
        ElMessage.success('已删除')
        await loadList()
        refreshTopicSelection()
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('删除失败: ' + (e.message || ''))
      }
    }

    // ── 版本 ──
    const loadVersionDrawer = async (item) => {
      if (!item?.id) return
      versionDrawerVisible.value = true
      loadingVersions.value = true
      diffVersionId.value = null
      try {
        const res = await promptApi.getVersions(item.id)
        versions.value = res.versions || []
      } catch (e) {
        ElMessage.error('加载版本失败: ' + (e.message || ''))
      } finally {
        loadingVersions.value = false
      }
    }

    const openVersionDrawer = async () => {
      versionDrawerTarget.value = 'framework'
      await loadVersionDrawer(selected.value)
    }

    const openTopicVersionDrawer = async () => {
      versionDrawerTarget.value = 'topic'
      await loadVersionDrawer(selectedTopicItem.value)
    }

    // 逐行对比（不做真正的 LCS diff，仅按行号对齐标注差异，够用且够快）
    const currentVersionNo = computed(() => {
      const cur = versions.value.find(v => v.is_current)
      const item = versionDrawerItem.value
      return cur ? cur.version_no : (item?.current_version_no || 0)
    })

    const toggleDiff = (v) => {
      diffVersionId.value = diffVersionId.value === v.id ? null : v.id
    }

    const prefixForType = (t) => ({ add: '+', del: '-', same: ' ' }[t] || ' ')

    const renderDiff = (oldContent) => {
      const fallback = versionDrawerTarget.value === 'topic'
        ? editTopicForm.content
        : editForm.content
      const cur = versions.value.find(v => v.is_current)?.content || fallback || ''
      const oldLines = (oldContent || '').split('\n')
      const newLines = cur.split('\n')
      // 简易"按公共前缀/后缀剥离"的行级差异
      const max = Math.max(oldLines.length, newLines.length)
      const out = []
      for (let i = 0; i < max; i++) {
        const a = oldLines[i]
        const b = newLines[i]
        if (a === b) {
          if (a !== undefined) out.push({ type: 'same', text: a })
        } else {
          if (a !== undefined) out.push({ type: 'del', text: a })
          if (b !== undefined) out.push({ type: 'add', text: b })
        }
      }
      return out
    }

    const doRollback = async (versionNo) => {
      const item = versionDrawerItem.value
      if (!item?.id) return
      try {
        await ElMessageBox.confirm(`确定回滚到 v${versionNo}？将作为新版本写入。`, '回滚', { type: 'warning' })
        await promptApi.rollback(item.id, versionNo)
        ElMessage.success('回滚成功')
        versionDrawerVisible.value = false
        await loadList()
        if (versionDrawerTarget.value === 'topic') {
          refreshTopicSelection()
        }
      } catch (e) {
        if (e !== 'cancel') ElMessage.error('回滚失败: ' + (e.message || ''))
      }
    }

    // ── 预览 ──
    const runPreview = async () => {
      previewing.value = true
      try {
        // 知乎问答：按当前选中 segment 拼示例
        if (activeScope.value === 'zhihu_qa') {
          const seg = selected.value?.segment
          if (!seg) {
            ElMessage.warning('请先在左栏选中一个片段（联网检索 / 拆方向 / 写正文）再预览')
            return
          }
          const payload = {
            segment: seg,
            question: zhihuPreview.question,
            extra_hint: zhihuPreview.extra_hint,
            direction_label: zhihuPreview.direction_label,
            direction_text: zhihuPreview.direction_text,
            sources_text: zhihuPreview.sources_text,
          }
          if (seg === 'article_prompt') {
            // 写正文：使用当前选中变体的 scope_key 作为 variant key
            payload.article_prompt_key = (selected.value?.scope_key || '').trim()
          }
          const res = await zhihuQaApi.previewPrompts(payload)
          previewResult.value = { system: res.system || '', user: res.user || '' }
          return
        }

        // 专栏口播：按当前选中 segment 拼示例
        if (activeScope.value === 'column_oral') {
          const seg = selected.value?.segment
          if (!seg) {
            ElMessage.warning('请先在左栏选中一个片段（联网检索 / 写口播稿）再预览')
            return
          }
          const payload = {
            segment: seg,
            topic: columnOralPreview.topic,
            direction: columnOralPreview.direction,
            theory: columnOralPreview.theory,
            cases: columnOralPreview.cases,
            extra_hint: columnOralPreview.extra_hint,
            sources_text: columnOralPreview.sources_text,
          }
          if (seg === 'article_prompt') {
            payload.article_prompt_key = (selected.value?.scope_key || '').trim()
          }
          const res = await columnOralApi.previewPrompts(payload)
          previewResult.value = { system: res.system || '', user: res.user || '' }
          return
        }

        // Tabstory：用一组 pill 表单
        if (activeScope.value === 'tabstory') {
          const res = await tabstoryApi.previewPrompts({
            post_type: tabstoryPreview.post_type,
            audience: tabstoryPreview.audience,
            angle: tabstoryPreview.angle,
            tone: tabstoryPreview.tone,
            language: tabstoryPreview.language,
            keywords: tabstoryPreview.keywords.slice(),
            question: tabstoryPreview.post_type === 'B' ? '' : tabstoryPreview.question,
          })
          previewResult.value = { system: res.system || '', user: res.user || '' }
          return
        }

        // B站洗稿：把视频元信息 + 转写稿 + 附加风格回灌到 system_prompt / user_template
        if (activeScope.value === 'bilibili_rewrite') {
          const res = await bilibiliRewriteApi.previewPrompts({
            title: biliRewritePreview.title,
            uploader: biliRewritePreview.uploader,
            duration: biliRewritePreview.duration,
            url: biliRewritePreview.url,
            transcript: biliRewritePreview.transcript,
            transcript_source: biliRewritePreview.transcript_source,
            style_hint: biliRewritePreview.style_hint,
          })
          previewResult.value = { system: res.system || '', user: res.user || '' }
          return
        }

        if (activeScope.value === 'douyin_rewrite') {
          const res = await douyinRewriteApi.previewPrompts({
            title: douyinRewritePreview.title,
            uploader: douyinRewritePreview.uploader,
            duration: douyinRewritePreview.duration,
            url: douyinRewritePreview.url,
            transcript: douyinRewritePreview.transcript,
            transcript_source: douyinRewritePreview.transcript_source,
            style_hint: douyinRewritePreview.style_hint,
          })
          previewResult.value = { system: res.system || '', user: res.user || '' }
          return
        }

        if (activeScope.value === 'content_rewrite') {
          const res = await rewriteApi.previewPrompts({
            original_content: rewritePreview.original_content,
            intensity: rewritePreview.intensity,
            length: rewritePreview.length,
            keywords: rewritePreview.keywords,
            custom_prompt: rewritePreview.custom_prompt,
            version_index: rewritePreview.version_index,
          })
          previewResult.value = { system: res.system || '', user: res.user || '' }
          return
        }

        // 默认（global / article_type）：原文章类型预览
        if (!preview.article_type) {
          ElMessage.warning('请先选择文章类型')
          return
        }
        const payload = {
          platform: preview.platform || 'xiaohongshu',
          article_type: preview.article_type,
          prompt_type: 'author',
          angle_seed: 0,
          market: contentMarket.value,
        }
        const filtered = (preview.mock_materials || []).filter(
          m => (m.title && m.title.trim()) || (m.content && m.content.trim())
        )
        if (filtered.length) {
          payload.mock_materials = filtered
        }
        const res = await promptApi.preview(payload)
        previewResult.value = { system: res.system, user: res.user }
      } catch (e) {
        ElMessage.error('预览失败: ' + (e.message || ''))
      } finally {
        previewing.value = false
      }
    }

    const copyText = async (text) => {
      try {
        await navigator.clipboard.writeText(text || '')
        ElMessage.success('已复制')
      } catch (e) {
        ElMessage.error('复制失败')
      }
    }

    // ═══════════════════════════════════════════════════════════════
    // 顶级 Tab 切换：提示词模板 / 话题池
    // ═══════════════════════════════════════════════════════════════

    const topTab = ref(props.mode === 'topic_pool' ? 'topic_pool' : 'template')
    const showTemplatePane = computed(() => props.mode === 'templates' || props.mode === 'all')
    const showTopicPane = computed(() => props.mode === 'topic_pool' || props.mode === 'all')
    /** 国内 / 海外：影响提示词列表、预览、话题池分区 */
    const contentMarket = ref('domestic')

    async function onContentMarketChange() {
      selected.value = null
      if (contentMarket.value === 'overseas') {
        preview.platform = 'reddit'
        if (String(preview.article_type || '').startsWith('T-')) {
          preview.article_type = 'OT-01'
        }
      } else {
        preview.platform = 'xiaohongshu'
        if (String(preview.article_type || '').startsWith('OT-')) {
          preview.article_type = 'T-13'
        }
      }
      await loadList()
      if (loadedTabs.topic_pool) {
        await loadTpArticleTypes()
        sanitizeTpPoolSelections()
        await loadTopicPool()
      }
    }

    const previewTypeOptions = computed(() => {
      if (contentMarket.value === 'overseas') {
        return meta.value.article_types_overseas || []
      }
      return meta.value.article_types || []
    })

    // 话题池 state & 方法
    // ═══════════════════════════════════════════════════════════════
    const tpItems = ref([])
    const tpLoading = ref(false)
    const tpStats = ref({})
    const tpFilter = reactive({ article_type: '', status: '', days: null })
    const tpArticleTypes = ref({})  // {T-13: {name: '...'}, ...}

    // 生成对话框
    const tpGenerateDialog = reactive({
      visible: false,
      running: false,
      jobId: null,
      jobStatus: '',
      progressPct: 0,
      progressMsg: '',
      _pollTimer: null,
      form: {
        article_types: [],
        total_count: 10,
        search_materials: true,
        material_budget: 5,
        market: 'domestic',
        theme_ip: '',
        theme_guidance: '',
      },
    })
    // 手动录入对话框
    const tpManualDialog = reactive({
      visible: false,
      saving: false,
      form: { article_type: '', topic_text: '', search_query: '' },
    })

    const tpMaterialsDialog = reactive({
      visible: false,
      articleType: '',
      topicText: '',
      searchQuery: '',
      materials: [],
    })

    function openTpMaterialsDialog(row) {
      tpMaterialsDialog.articleType = row.article_type || ''
      tpMaterialsDialog.topicText = row.topic_text || ''
      tpMaterialsDialog.searchQuery = row.search_query || ''
      let m = row.materials
      if (typeof m === 'string') {
        try {
          m = JSON.parse(m)
        } catch {
          m = []
        }
      }
      tpMaterialsDialog.materials = (Array.isArray(m) ? m : []).map((item, i) => {
        if (item != null && typeof item === 'object') return item
        return { title: `素材 ${i + 1}`, content: String(item ?? '') }
      })
      tpMaterialsDialog.visible = true
    }

    async function loadTopicPoolStats() {
      try {
        const mk = contentMarket.value === 'overseas' ? 'overseas' : 'domestic'
        tpStats.value = await topicPoolApi.getStats({ market: mk })
      } catch (e) {}
    }

    async function loadTopicPool() {
      tpLoading.value = true
      try {
        const params = {}
        params.market = contentMarket.value === 'overseas' ? 'overseas' : 'domestic'
        if (tpFilter.article_type) params.article_type = tpFilter.article_type
        if (tpFilter.status) params.status = tpFilter.status
        if (tpFilter.days) params.days = tpFilter.days
        params.limit = 200
        const res = await topicPoolApi.getList(params)
        tpItems.value = res.items || []
      } catch (e) {
        ElMessage.error('加载话题池失败')
      } finally {
        tpLoading.value = false
      }
      loadTopicPoolStats()
    }

    async function loadTpArticleTypes() {
      try {
        const res = await import('axios').then(a => a.default.get('/api/config'))
        const d = res.data.article_types || {}
        const o = res.data.article_types_overseas || {}
        tpArticleTypes.value = contentMarket.value === 'overseas' ? o : d
      } catch (e) {}
    }

    /** 批量生成里已选类型：切换国内/海外后去掉另一套市场的键（否则 OT 界面下仍会提交隐藏的 T-xx） */
    function sanitizeTpPoolSelections() {
      const valid = new Set(Object.keys(tpArticleTypes.value || {}))
      tpGenerateDialog.form.article_types = (tpGenerateDialog.form.article_types || []).filter((k) =>
        valid.has(k),
      )
      if (tpFilter.article_type && !valid.has(tpFilter.article_type)) {
        tpFilter.article_type = ''
      }
    }

    async function openTpGenerateDialog() {
      await loadTpArticleTypes()
      sanitizeTpPoolSelections()
      tpGenerateDialog.visible = true
      tpGenerateDialog.jobId = null
      tpGenerateDialog.jobStatus = ''
      tpGenerateDialog.progressPct = 0
      tpGenerateDialog.progressMsg = ''
      tpGenerateDialog.running = false
      tpGenerateDialog.form.market = contentMarket.value === 'overseas' ? 'overseas' : 'domestic'
    }

    async function submitTpGenerate() {
      if (!tpGenerateDialog.form.article_types.length) return
      tpGenerateDialog.running = true
      tpGenerateDialog.jobId = null
      try {
        const res = await topicPoolApi.generate(tpGenerateDialog.form)
        const jobId = res.job_id
        tpGenerateDialog.jobId = jobId
        tpGenerateDialog.progressMsg = '生成中...'
        // 轮询进度
        const poll = async () => {
          try {
            const s = await topicPoolApi.getGenerateStatus(jobId)
            tpGenerateDialog.jobStatus = s.status
            tpGenerateDialog.progressMsg = s.message || ''
            const done = s.done || 0
            const total = s.total || tpGenerateDialog.form.total_count
            tpGenerateDialog.progressPct = total > 0 ? Math.round((done / total) * 100) : 0
            if (s.status === 'completed') {
              tpGenerateDialog.running = false
              ElMessage.success(s.message || '话题生成完成')
              await loadTopicPool()
            } else if (s.status === 'failed') {
              tpGenerateDialog.running = false
              ElMessage.error('生成失败：' + (s.error || ''))
            } else {
              tpGenerateDialog._pollTimer = setTimeout(poll, 2000)
            }
          } catch (e) {
            tpGenerateDialog.running = false
          }
        }
        poll()
      } catch (e) {
        tpGenerateDialog.running = false
        ElMessage.error('启动失败：' + (e.response?.data?.detail || e.message))
      }
    }

    function openTpManualDialog() {
      tpManualDialog.visible = true
      tpManualDialog.form = { article_type: '', topic_text: '', search_query: '' }
    }

    async function submitTpManual() {
      if (!tpManualDialog.form.article_type || !tpManualDialog.form.topic_text) return
      tpManualDialog.saving = true
      try {
        await topicPoolApi.addManual({
          ...tpManualDialog.form,
          market: contentMarket.value === 'overseas' ? 'overseas' : 'domestic',
        })
        ElMessage.success('已录入')
        tpManualDialog.visible = false
        await loadTopicPool()
      } catch (e) {
        ElMessage.error('录入失败：' + (e.response?.data?.detail || e.message))
      } finally {
        tpManualDialog.saving = false
      }
    }

    async function resetTpStatus(row) {
      try {
        await topicPoolApi.resetStatus(row.id, 'unused')
        ElMessage.success('已重置为未使用')
        await loadTopicPool()
      } catch (e) {
        ElMessage.error('操作失败')
      }
    }

    async function reserveTpEntry(row) {
      try {
        await topicPoolApi.resetStatus(row.id, 'reserved')
        ElMessage.success('已标为保留')
        await loadTopicPool()
      } catch (e) {
        ElMessage.error('操作失败')
      }
    }

    async function deleteTpEntry(row) {
      try {
        await topicPoolApi.delete(row.id)
        ElMessage.success('已删除')
        await loadTopicPool()
      } catch (e) {
        ElMessage.error('删除失败')
      }
    }

    const loadedTabs = reactive({ topic_pool: false })
    function onTopTabChange(name) {
      if (name === 'topic_pool' && !loadedTabs.topic_pool) {
        loadedTabs.topic_pool = true
        loadTpArticleTypes()
        loadTopicPool()
      }
    }

    onMounted(async () => {
      await loadMeta()
      if (showTemplatePane.value) {
        await loadList()
      }
      loadTpArticleTypes()
      loadBuiltinTopicPrompts()
      if (showTopicPane.value) {
        loadedTabs.topic_pool = true
        await loadTopicPool()
      }
      // 进入页面如果默认 scope 已是 tabstory，则提前预热选项
      if (activeScope.value === 'tabstory') {
        loadTabstoryPreviewOptions()
      }
    })

    return {
      // data
      activeScope, search, items, selected, loadingList, saving, meta,
      editForm, preview, previewResult, previewTab, previewing,
      zhihuPreview, columnOralPreview, tabstoryPreview, tabstoryOptions, biliRewritePreview, douyinRewritePreview, rewritePreview,
      togglePreviewKeyword, loadTabstoryPreviewOptions,
      createDialogVisible, createForm, creating,
      versionDrawerVisible, versions, loadingVersions, diffVersionId,
      // computed
      scopeOptions, currentScopeLabel, filteredItems, ZHIHU_QA_SEGMENT_HINTS, COLUMN_ORAL_SEGMENT_HINTS, TABSTORY_SEGMENT_HINTS, BILI_REWRITE_SEGMENT_HINTS, DOUYIN_REWRITE_SEGMENT_HINTS, CONTENT_REWRITE_SEGMENT_HINTS,
      availablePlaceholders, currentCreateScopeLabel,
      createScopeKeyOptions, createSegmentOptions, currentVersionNo,
      canDeleteCurrent, deleteButtonText, previewTypeOptions,
      // methods
      itemKey, displayKey, segmentLabel, sourceLabel, sourceTagType, formatTime,
      loadList, selectItem, onScopeChange,
      saveEdit, resetToBuiltin, deleteCurrent,
      openCreateDialog, onCreateScopeChange, submitCreate,
      editAngles, selectedAnglesItem, savingAngles,
      addEntryAngle, removeEntryAngle, saveEntryAngles,
      selectedTopicItem, editTopicForm, savingTopic, saveTopicEdit,
      topicSource, canDeleteTopic, deleteTopicButtonText,
      openTopicVersionDrawer, resetTopicToBuiltin, deleteTopicCurrent,
      versionDrawerTarget,
      createTypeDialogVisible, createTypeForm, creatingType, submitCreateType,
      openVersionDrawer, doRollback, runPreview, copyText,
      toggleDiff, renderDiff, prefixForType,
      addMaterial, removeMaterial,
      // —— 词库：顶级 Tab ——
      mode: props.mode,
      showTemplatePane, showTopicPane,
      topTab, onTopTabChange, contentMarket, onContentMarketChange,
      // —— 话题池 ——
      tpItems, tpLoading, tpStats, tpFilter, tpArticleTypes,
      tpGenerateDialog, tpManualDialog, tpMaterialsDialog,
      loadTopicPool, openTpGenerateDialog, submitTpGenerate,
      openTpManualDialog, submitTpManual, openTpMaterialsDialog,
      resetTpStatus, reserveTpEntry, deleteTpEntry,
      // icons
      Plus, Refresh, Check, Clock, RefreshLeft, Delete, View, CopyDocument, Search,
    }
  }
}
</script>

<style scoped>
.prompts-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  gap: 12px;
}

.market-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 4px;
}
.market-label {
  font-size: 13px;
  color: #606266;
}

.scope-tabs {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-radius: 4px;
}

.toolbar-right {
  display: flex;
  gap: 8px;
}

.main-layout {
  display: grid;
  grid-template-columns: 320px 1fr 420px;
  gap: 12px;
  /* 顶级 tab 化后，用 min-height 保证三栏完整可见（其余部分由 tab-content 滚动） */
  min-height: calc(100vh - 200px);
}

.col {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.col :deep(.el-card__body) {
  flex: 1;
  overflow: auto;
  padding: 12px;
}

.col-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  gap: 6px;
}

/* 左栏 */
.item-list { display: flex; flex-direction: column; gap: 4px; }
.item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all .15s;
}
.item-row:hover { background: #f5f7fa; }
.item-row.active { background: #ecf5ff; border-color: #409eff; }
.item-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.item-title {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 190px;
}
.item-segment {
  font-size: 12px;
  color: #909399;
}

/* 中栏 */
.placeholder {
  padding: 40px 20px;
  text-align: center;
  color: #909399;
}
.editor { display: flex; flex-direction: column; gap: 8px; }
.action-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 4px;
}
.placeholder-hint { margin-top: 12px; }

.angles-empty {
  color: #909399;
  font-size: 13px;
  margin-bottom: 10px;
}
.angle-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fafafa;
}
.angle-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.angles-actions { margin-bottom: 8px; }

/* 右栏 */
.preview-form { margin-bottom: 8px; }
.preview-form .hint { margin-left: 8px; color: #909399; font-size: 12px; }
.preview-form .hint-text { color: #909399; font-size: 12px; }
.ts-preview-kw-group { display: flex; flex-wrap: wrap; }
.preview-tabs :deep(.el-tabs__content) { padding: 0; }
.preview-box {
  position: relative;
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 12px;
  border-radius: 4px;
  min-height: 260px;
  max-height: calc(100vh - 520px);
  overflow: auto;
}
.preview-box pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 12px;
  line-height: 1.6;
}
.preview-empty {
  color: #606266;
  text-align: center;
  padding: 60px 0;
}
.copy-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
}

/* 版本抽屉 */
.version-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.version-note { color: #606266; font-size: 13px; }
.version-content {
  background: #f5f7fa;
  padding: 10px;
  border-radius: 4px;
  white-space: pre-wrap;
  font-size: 12px;
  max-height: 360px;
  overflow: auto;
}

.diff-view {
  margin-top: 8px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  max-height: 420px;
  overflow: auto;
  background: #fafbfc;
}
.diff-title {
  padding: 6px 10px;
  font-size: 12px;
  color: #606266;
  background: #f2f3f5;
  border-bottom: 1px solid #e4e7ed;
}
.diff-lines {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 12px;
  line-height: 1.55;
}
.diff-lines > div {
  display: flex;
  padding: 1px 8px;
  white-space: pre-wrap;
  word-break: break-word;
}
.diff-prefix {
  width: 16px;
  flex-shrink: 0;
  color: #909399;
  user-select: none;
}
.diff-text { flex: 1; }
.diff-add { background: #ecf9ec; }
.diff-add .diff-text { color: #1d7d3c; }
.diff-del { background: #fdecea; }
.diff-del .diff-text { color: #b72926; text-decoration: line-through; }
.diff-same { color: #606266; }

/* 预览 Mock 素材区 */
.mock-collapse { margin: 4px 0 10px; }
.mock-title { font-size: 13px; color: #606266; }
.mock-list { display: flex; flex-direction: column; gap: 10px; }
.mock-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
}
.hint { font-size: 12px; color: #909399; }

/* ═══════════ 顶级 Tab（提示词模板 / 话题池） ═══════════ */
.top-tabs.single-mode :deep(.el-tabs__header) {
  display: none;
}
.top-tabs {
  background: #fff;
  border-radius: 6px;
  padding: 0 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.top-tabs :deep(.el-tabs__content) {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 8px 4px 16px;
}

.top-tabs :deep(.el-tabs__item) {
  font-size: 15px;
  font-weight: 500;
  height: 44px;
  line-height: 44px;
}

/* 词库 Tab 内部 */
.dict-tab {
  padding: 8px 4px;
}

.dict-tab .tab-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0 14px;
}

.dict-tab .toolbar-spacer { flex: 1; }

.dict-tab .topic-name { font-weight: 500; }

.dict-tab .long-tail-cell { line-height: 1.8; }

.dict-tab .muted { color: #c0c4cc; font-size: 12px; }

.dict-tab .mono {
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 12px;
  color: #606266;
}

/* 长尾词/关键词编辑器 */
.kw-editor {
  min-height: 40px;
  padding: 6px;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
}

.form-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.5;
}

/* 话题池：素材条数可点开查看正文 */
.tp-mat-trigger {
  cursor: pointer;
  user-select: none;
}
.tp-mat-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.tp-mat-search {
  color: #409eff;
  font-size: 12px;
}
.tp-mat-topic {
  margin: 0 0 14px;
  font-size: 13px;
  color: #303133;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.tp-mat-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 10px;
  background: #fafafa;
}
.tp-mat-card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.tp-mat-card-body {
  font-size: 13px;
  line-height: 1.65;
  color: #606266;
  white-space: pre-wrap;
  word-break: break-word;
}
.tp-mat-kw {
  margin-top: 8px;
}
</style>
